import { Injectable } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import { ResultLayer } from '../model/result-layers';

@Injectable({
  providedIn: 'root'
})
export class ResultMapLayerCreationService {

  create(mapbox: mapboxgl.Map, sourceName: string) {

    mapbox.addLayer({
      id: ResultLayer.BOX,
      type: 'line',
      source: sourceName,
      layout: { visibility: 'visible' },
      filter: ['==', 'element-type', 'box'],
      paint: {
        'line-color': [
          'match',
          ['get', 'model'],
          'CTOP 90/10',
          '#00ff00',
          'CTOP 85/15',
          '#ffff00',
          'CTOP 80/20',
          '#ffffff',
          'CTOP 70/30',
          '#0000ff',
          'CTOP 60/40',
          '#ff0000',
          'CTOP Simetrica',
          '#cccccc',
          'FIST-144',
          '#ff9900',
          'FIST-72',
          '#ff4000',
          'FOSC-72',
          '#ff9900',
          'FOSC-48',
          '#999933',
          '#993333' /* other */
        ],
        'line-width': 5


      }
    });


    mapbox.addLayer({
      id: ResultLayer.VERTEX,
      type: 'line',
      layout: { visibility: 'visible' },
      source: sourceName,
      filter: ['==', 'element-type', 'vertex'],
      paint: {
        'line-color': [
          'match',
          ['get', 'class'],
          'CO',
          '#fbb03b',
          'PT',
          '#223b53',
          'CA',
          '#e55e5e',
          'CS',
          '#3bb2d0',
          '#ccc' /* other */
        ],
        'line-width': 3
      }
    });

    mapbox.addLayer({
      id: ResultLayer.TRUNK_CABLES,
      type: 'line',
      layout: { visibility: 'visible' },
      source: sourceName,
      filter: ['all', ['==', 'element-type', 'cable'], ['==', 'type', 'trunk']],
      paint: {
        'line-color': [
          'match',
          ['get', 'type'],
          'trunk',
          '#ff0000',
          'ctop',
          '#eb34cc',
          'cdoi',
          '#34ebdb',
          'dist-24',
          '#0000ff',
          'dist-12',
          '#f2fc23',
          '#ffffff' /* other */
        ],
        'line-width': 4
      }
    });

    mapbox.addLayer({
      id: ResultLayer.DIST_CABLES,
      type: 'line',
      source: sourceName,
      layout: { visibility: 'visible' },
      filter: ['all',
        ['==', 'element-type', 'cable'],
        ['any',
          ['==', 'type', 'dist-12'],
          ['==', 'type', 'dist-24']
        ]
      ],
      paint: {
        'line-color': [
          'match',
          ['get', 'type'],
          'trunk',
          '#ff0000',
          'ctop',
          '#eb34cc',
          'cdoi',
          '#34ebdb',
          'dist-24',
          '#0000ff',
          'dist-12',
          '#f2fc23',
          '#ffffff'/* other */
        ],
        'line-width': 4
      }
    });


    mapbox.addLayer({
      id: ResultLayer.CTOP_CABLES,
      type: 'line',
      source: sourceName,
      layout: { visibility: 'visible' },
      filter: ['all', ['==', 'element-type', 'cable'], ['==', 'type', 'ctop']],
      paint: {
        'line-color': [
          'match',
          ['get', 'type'],
          'trunk',
          '#ff0000',
          'ctop',
          '#eb34cc',
          'cdoi',
          '#34ebdb',
          'dist-24',
          '#0000ff',
          'dist-12',
          '#f2fc23',
          '#ffffff'/* other */
        ],
        'line-width': 4,
        'line-dasharray': [4, 1]
      }
    });

    mapbox.addLayer({
      id: ResultLayer.CDOI_CABLES,
      type: 'line',
      layout: { visibility: 'visible' },
      source: sourceName,
      filter: ['all', ['==', 'element-type', 'cable'], ['==', 'type', 'cdoi']],
      paint: {
        'line-color': [
          'match',
          ['get', 'type'],
          'trunk',
          '#ff0000',
          'ctop',
          '#eb34cc',
          'cdoi',
          '#34ebdb',
          'dist-24',
          '#0000ff',
          'dist-12',
          '#f2fc23',
          '#ffffff' /* other */
        ],
        'line-width': 4,
        'line-dasharray': [3, 2]
      }
    });

    mapbox.addLayer({
      id: ResultLayer.CABLE_LABELS,
      type: 'symbol',
      source: sourceName,
      layout: {
        'symbol-placement': 'line',
        'text-font': ['Open Sans Regular'],
        'text-field': '{name}',
        'text-size': 12,
        visibility: 'visible'
      },
      paint: {
        'text-color': '#ffffcc'
      }
    });

    mapbox.addLayer({
      id: ResultLayer.BOX_LABELS,
      type: 'symbol',
      source: sourceName,
      filter: ['==', 'element-type', 'box-label'],
      minzoom: 17,
      layout: {
        'symbol-placement': 'point',
        'text-font': ['Open Sans Regular'],
        'text-field': '{name}',
        'text-size': [
          'interpolate',
          ['exponential', 2],
          ['zoom'],
          17,
          4,
          22,
          24
        ],
        visibility: 'visible'
      },
      paint: {
        'text-color': '#ffffcc'
      }
    });

    mapbox.addLayer({
      id: ResultLayer.VERTEX_LABELS,
      type: 'symbol',
      source: sourceName,
      filter: ['==', 'element-type', 'vertex-label'],
      minzoom: 17,
      layout: {
        'symbol-placement': 'point',
        'text-font': ['Open Sans Regular'],
        'text-field': '{label}',
        'text-size': [
          'interpolate',
          ['exponential', 2],
          ['zoom'],
          17,
          4,
          22,
          24
        ],
        visibility: 'visible'
      },
      paint: {
        'text-color': '#ffffcc'

      }
    });

    mapbox.addLayer({
      id: ResultLayer.BUILDING_LABELS,
      type: 'symbol',
      source: sourceName,
      filter: ['==', 'element-type', 'building-label'],
      minzoom: 15,
      layout: {
        'symbol-placement': 'point',
        'text-font': ['Open Sans Regular'],
        'text-field': '{label}',
        'text-size': [
          'interpolate',
          ['exponential', 2],
          ['zoom'],
          15,
          5,
          22,
          28
        ],
        visibility: 'visible'
      },
      paint: {
        'text-color': [
          'match',
          ['get', 'covered'],
          'yes',
          '#00ccff',
          '#ffcc00'
        ]
      }
    });

  }

}
